import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import React from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, LabelM } from '@sorare/core/src/atoms/typography';
import { OpenItemDialogLink } from '@sorare/core/src/components/link/OpenItemDialogLink';
import { getHumanReadableSerialNumber } from '@sorare/core/src/lib/cards';

import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { useClickCardEvent } from '#src/hooks/events/useClickCardEvent.ts';

import type { CardPreview_anyCard } from './__generated__/index.graphql.ts';

const CardContainer = styled(Vertical)`
  height: 100%;
  padding: var(--double-unit);
  background-color: var(--c-nd-100);
  flex: 1;
  border-radius: var(--unit);
`;
const CardInfoContainer = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-start;
`;
const PlayerName = styled(BodyL)`
  color: var(--c-white);
`;
const TokenDetails = styled(LabelM)`
  color: var(--c-white);
`;

export const CardPreview = ({
  card,
  cardPositions,
  cardProperties,
}: {
  card: CardPreview_anyCard;
  cardPositions?: ReactNode;
  cardProperties: ReactNode;
}) => {
  const trackClickCard = useClickCardEvent();

  return (
    <OpenItemDialogLink
      item={card}
      onClick={() => trackClickCard(card, false)}
      sport={card.sport}
    >
      <CardContainer key={card.slug}>
        <FlexCard card={card} />
        <CardInfoContainer>
          {cardProperties}
          <div>
            <PlayerName>
              <strong>{card.anyPlayer.displayName}</strong>
            </PlayerName>
            <TokenDetails>
              {cardPositions}
              {getHumanReadableSerialNumber(card, {
                separator: ' • ',
              })}
            </TokenDetails>
          </div>
        </CardInfoContainer>
      </CardContainer>
    </OpenItemDialogLink>
  );
};

CardPreview.fragments = {
  anyCard: gql`
    fragment CardPreview_anyCard on AnyCardInterface {
      slug
      sport
      anyPlayer {
        slug
        displayName
      }
      ...FlexCard_anyCard
      ...getHumanReadableSerialNumber_anyCard
      ...useClickCardEvent_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${getHumanReadableSerialNumber.fragments.anyCard}
    ${useClickCardEvent.fragments.anyCard}
  ` as TypedDocumentNode<CardPreview_anyCard>,
};
